import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { FilterOption } from 'components/Filters/FilterOption/FilterOption';
import { Virtuoso } from 'react-virtuoso';
import * as Styled from './filterSubItems.styles';
import { FilterSubItemsProps, RenderedFiltersData } from './types';
import { useFiltersContext } from '../../../contexts';
import { FilterData } from '../../../types';
import {
  areComponentPropsEqual,
  splittedCategoriesFields,
} from '../../../utils';

const FilterSubItems = React.memo((props: FilterSubItemsProps) => {
  const {
    renderedFilters,
    onFilterClicked,
    currentParentItems,
    isMobile,
    onMobileOpenFilter,
    mobileActiveFilter,
    appFilters,
  } = props;
  const [multilevelFilterSelected, setMultilevelFilterSelected] =
    useState(false);

  const { createFilter, getFiltersByPropertyValue } = useFiltersContext();

  const getIsParentChecked = useCallback(
    (parentLabel: string, field = '', isStarting = true) => {
      return Boolean(
        getFiltersByPropertyValue(
          'value',
          parentLabel,
          {
            isStarting: isStarting,
            field,
          },
          appFilters,
        ).length,
      );
    },
    [appFilters, getFiltersByPropertyValue],
  );

  const isAnyParentChecked = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderedFilters.some(([_label, data]) => {
        return Boolean(data?.active);
      }),
    [renderedFilters],
  );

  useEffect(() => {
    if (isAnyParentChecked && !multilevelFilterSelected) {
      setMultilevelFilterSelected(true);
    }
  }, [isAnyParentChecked, multilevelFilterSelected]);

  // Function to filter filters, if sibling selected
  const handleFilterItems = (data: RenderedFiltersData) => {
    if (
      isAnyParentChecked &&
      multilevelFilterSelected &&
      data.multiple !== 'single' &&
      data?.field != null &&
      splittedCategoriesFields.includes(data?.field)
    ) {
      return Boolean(data?.active);
    } else {
      return true;
    }
  };

  const handleSelectFilter = (filter: FilterData, isParent: boolean) => {
    if (isMobile && isParent) {
      return;
    }
    setMultilevelFilterSelected(isParent);
    onFilterClicked(filter, isParent);
  };

  const getButtonId = (item: FilterData, label?: string) => {
    const labelText = label ?? item?.label;
    const labelChild = labelText?.includes('>')
      ? labelText?.split('>')?.[1]
      : labelText;
    const itemLabel = labelChild?.replace(' ', '_')?.toLowerCase();
    return `${item.field}_${itemLabel}_filter_checkbox`;
  };

  const handleParentCheckboxMobileClick = (filter: FilterData) => {
    if (isMobile) {
      setMultilevelFilterSelected(true);
      onFilterClicked(filter, true);
    }
  };

  const handlePrentLabelMobileClick = (
    filter: FilterData,
    values: Array<[string, FilterData]>,
  ) => {
    if (isMobile) {
      onMobileOpenFilter?.({
        ...filter,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        values: values.map(([_, value]) => value),
      });
      if (!appFilters[filter.id]) {
        setMultilevelFilterSelected(true);
        onFilterClicked(filter, true);
      }
    }
  };

  const filteredFilters = renderedFilters.filter(
    (item: [string, RenderedFiltersData]) => handleFilterItems(item?.[1]),
  );
  const itemsLengthDesktop =
    currentParentItems?.length ?? filteredFilters.length;
  const itemsLengthMobile = filteredFilters.some(
    ([filter]) => filter === mobileActiveFilter?.label,
  )
    ? filteredFilters[0]?.[1]?.items?.length
    : filteredFilters.length;
  const itemsLength = isMobile ? itemsLengthMobile : itemsLengthDesktop;
  const desktopVirtuosoHeight = itemsLength > 10 ? 400 : itemsLength * 40;
  const mobileVirtuosoHeight =
    itemsLength > 10 ? 'calc(100vh - 243px)' : itemsLength * 40;

  return (
    <Styled.ItemWrapper>
      <Virtuoso
        data={filteredFilters}
        style={{
          height: isMobile ? mobileVirtuosoHeight : desktopVirtuosoHeight,
        }}
        itemContent={(
          index,
          [
            parentLabel,
            {
              count,
              items,
              originFilter,
              field,
              multiple,
              high = null,
              low = null,
              active,
            },
          ],
        ) => {
          const parentFilter = createFilter(field || '', parentLabel);
          const isParentChecked =
            getIsParentChecked(parentLabel, field) ||
            active ||
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            items?.some(([_, item]) => item.active);
          const checkboxIcon = multiple === 'single' ? 'circle' : 'square';

          const isRangeType = high != null || low != null;
          const filterItem = isRangeType ? originFilter : parentFilter;
          const isSelected = filterItem?.id
            ? Boolean(appFilters[filterItem.id])
            : false;

          if (items.length === 0) {
            return (
              <FilterOption
                key={parentLabel + index}
                isLoading={false}
                onClick={(filter) => handleSelectFilter(filter, false)}
                isSelected={isSelected}
                item={filterItem}
                count={count}
                checkboxVariant={checkboxIcon}
                buttonId={filterItem ? getButtonId(filterItem) : undefined}
              />
            );
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const numberOfCheckedItems = items.filter(([_, filter]) =>
            Boolean(appFilters[filter.id]),
          ).length;

          return (
            <Styled.Item
              key={parentLabel + index}
              isParentChecked={isParentChecked}
            >
              {(isMobile
                ? mobileActiveFilter?.label !== parentLabel
                : !isParentChecked) && (
                <FilterOption
                  onClick={(item) => handleSelectFilter(item, true)}
                  isSelected={isSelected}
                  item={parentFilter}
                  buttonId={getButtonId(parentFilter)}
                  count={count}
                  checkboxVariant={checkboxIcon}
                  rightIcon="arrow-right"
                  badgeNumber={isMobile ? numberOfCheckedItems : 0}
                  onCheckboxClick={handleParentCheckboxMobileClick}
                  onLabelClick={(filter) =>
                    handlePrentLabelMobileClick(filter, items)
                  }
                />
              )}
              {(isMobile
                ? mobileActiveFilter?.label === parentLabel
                : isParentChecked) &&
                items?.map((subFilter) => {
                  const [label, filter] = subFilter;
                  return (
                    <FilterOption
                      key={filter?.label}
                      onClick={(item) => onFilterClicked?.(item)}
                      isSelected={Boolean(appFilters[filter.id])}
                      label={label}
                      item={filter}
                      checkboxVariant={checkboxIcon}
                      buttonId={getButtonId(filter, label)}
                    />
                  );
                })}
            </Styled.Item>
          );
        }}
      />
    </Styled.ItemWrapper>
  );
}, areComponentPropsEqual);

FilterSubItems.displayName = 'FilterSubItems';

export { FilterSubItems };
